import type { AbilityAssignment, AbilityName, CreationPlan } from '@rollcall/contracts'
import { ABILITY_NAMES, pointBuySpending } from '@rollcall/rules'
import { useT } from './i18n-context.js'

/**
 * Характеристики при создании: три способа из источника (с. 21).
 *
 * Способов ровно три, и это не слоты, а варианты входа
 * (`abilityChoiceSchema` в contracts) — поэтому они перечислены здесь,
 * а не приходят с сервера списком. Числа при этом сервер присылает
 * все: стандартный набор, бюджет и таблицу стоимости, параметры
 * броска. Ни одного из этих чисел в клиенте нет.
 *
 * У двух способов из трёх работа одна и та же: есть **пул** из шести
 * чисел и есть **распределение** — какое число какой характеристике
 * достаётся. У стандартного набора пул задан правилом, у бросков его
 * заполняет игрок (а позже — роллер, D12: числа приходят снаружи,
 * движок кости не бросает). Поэтому пул и распределение разведены
 * явно: раньше строка ввода стояла рядом с полями характеристик,
 * и было непонятно, зачем она, если поля уже есть.
 *
 * Покупка очков устроена иначе: там не пул, а бюджет, и значение
 * набирается каждому отдельно. Остаток считает `packages/rules`:
 * это правило источника, а не оформление (CLAUDE.md, п. 5).
 */

export type AbilityMethodKey = 'standard_array' | 'point_buy' | 'rolls'

export const ABILITY_METHODS: readonly AbilityMethodKey[] = [
  'standard_array',
  'point_buy',
  'rolls',
]

/** У способа есть пул из шести чисел? */
function usesPool(method: AbilityMethodKey): boolean {
  return method === 'standard_array' || method === 'rolls'
}

/**
 * Что из пула уже разобрано.
 *
 * Считается по вхождениям, а не по множеству: в пуле бывают
 * одинаковые числа, и два числа 13 — это две раздачи, а не одна.
 */
export function poolRemainder(
  pool: readonly number[],
  assignment: AbilityAssignment,
  taken: readonly AbilityName[],
): number[] {
  const left = [...pool]
  for (const name of taken) {
    const at = left.indexOf(assignment[name])
    if (at !== -1) left.splice(at, 1)
  }
  return left
}

export function AbilitiesField({
  plan,
  method,
  assignment,
  assigned,
  rolls,
  problems,
  onMethod,
  onAssignment,
  onAssigned,
  onRolls,
  onRollPool,
  rolling = false,
}: {
  readonly plan: CreationPlan
  readonly method: AbilityMethodKey
  readonly assignment: AbilityAssignment
  /** Каким характеристикам число уже выдано: пустых быть не должно. */
  readonly assigned: readonly AbilityName[]
  readonly rolls: readonly number[]
  readonly problems: readonly string[]
  readonly onMethod: (method: AbilityMethodKey) => void
  readonly onAssignment: (assignment: AbilityAssignment) => void
  readonly onAssigned: (assigned: readonly AbilityName[]) => void
  readonly onRolls: (rolls: readonly number[]) => void
  /**
   * Бросить пул на сервере (D12). Не обязателен: поле работает
   * и без роллера — числа вводят руками, и это равноправный путь.
   */
  readonly onRollPool?: (() => void) | undefined
  readonly rolling?: boolean | undefined
}) {
  const t = useT()
  const methods = plan.abilities.methods
  const spending = pointBuySpending(methods, assignment)
  // Ноль в пуле — «ещё не введено»: настоящий бросок 4d6 меньше трёх
  // не даёт, так что спутать нельзя, а отдельного «пусто» в числовом
  // поле нет.
  const slots =
    method === 'rolls'
      ? Array.from({ length: methods.roll.count }, (_, index) => rolls[index] ?? 0)
      : methods.standardArray
  const pool = slots.filter((value) => value > 0)
  const left = poolRemainder(pool, assignment, assigned)

  const give = (name: AbilityName, value: number | null) => {
    if (value === null) {
      onAssigned(assigned.filter((item) => item !== name))
      return
    }
    onAssignment({ ...assignment, [name]: value })
    onAssigned(assigned.includes(name) ? assigned : [...assigned, name])
  }

  const invalid = problems.length > 0

  return (
    <fieldset
      className="app-slot"
      data-slot="abilities"
      data-invalid={invalid ? 'true' : undefined}
    >
      <legend className="app-slot-legend">{t('wizard.abilities.title')}</legend>

      <div
        className="app-slot-options"
        role="radiogroup"
        aria-label={t('wizard.abilities.method')}
      >
        {ABILITY_METHODS.map((key) => (
          <label key={key} className="app-option">
            <input
              type="radio"
              name="ability-method"
              value={key}
              checked={method === key}
              onChange={() => {
                onMethod(key)
              }}
            />
            <span>{t(`wizard.abilities.${key}`)}</span>
          </label>
        ))}
      </div>

      {usesPool(method) && (
        <section className="app-pool" data-testid="ability-pool">
          <h4 className="app-pool-title">{t('wizard.abilities.poolTitle')}</h4>
          <p className="app-slot-hint">
            {method === 'rolls'
              ? t('wizard.abilities.poolRollsHint', {
                  count: methods.roll.count,
                  dice: methods.roll.dice,
                  die: methods.roll.die,
                  keep: methods.roll.keep,
                })
              : t('wizard.abilities.poolFixedHint')}
          </p>
          {/* Порядок ввода ни на что не влияет: раздаёт игрок ниже. */}
          <p className="app-slot-hint">{t('wizard.abilities.poolOrderHint')}</p>

          {/* Бросок делает сервер (D12): кости на клиенте — это
              число, которому за столом верить не с чего. Ручной ввод
              рядом и остаётся равноправным. */}
          {method === 'rolls' && onRollPool !== undefined && (
            <div className="app-actions">
              <button
                type="button"
                className="app-button"
                disabled={rolling}
                onClick={onRollPool}
              >
                {t('wizard.abilities.rollPool')}
              </button>
            </div>
          )}

          <div className="app-pool-values">
            {slots.map((value, index) =>
              method === 'rolls' ? (
                <label key={index} className="app-field app-field-narrow">
                  <span>{t('wizard.abilities.poolItem', { index: index + 1 })}</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={value === 0 ? '' : value}
                    onChange={(event) => {
                      const next = [...slots]
                      next[index] = Number(event.target.value)
                      onRolls(next)
                    }}
                  />
                </label>
              ) : (
                <span key={index} className="app-pool-value">
                  {value}
                </span>
              ),
            )}
          </div>

          <p className="app-slot-hint" data-testid="ability-pool-left">
            {left.length === 0
              ? t('wizard.abilities.poolDone')
              : t('wizard.abilities.poolLeft', { values: left.join(', ') })}
          </p>
        </section>
      )}

      {method === 'point_buy' && (
        <p className="app-slot-hint" data-testid="point-buy-budget">
          {spending.remaining === null
            ? t('wizard.abilities.budget', {
                remaining: '—',
                budget: methods.pointBuy.budget,
              })
            : t('wizard.abilities.budget', {
                remaining: spending.remaining,
                budget: methods.pointBuy.budget,
              })}
        </p>
      )}

      {/* Подписи «кому какое значение» здесь нет намеренно: она стояла
          внутри сетки характеристик и занимала в ней ячейку, отчего
          карточки разъезжались. Пул выше и селекты у характеристик
          говорят то же самое. */}
      <div className="app-abilities" data-testid="ability-assignment">
        {ABILITY_NAMES.map((name: AbilityName) => (
          <label key={name} className="app-field">
            <span>{t(`abilities.${name}`)}</span>
            {usesPool(method) ? (
              // Выбор из оставшегося: одно число двум характеристикам
              // не достанется — не потому, что проверка ругнётся,
              // а потому, что второй раз его в списке нет.
              <select
                value={assigned.includes(name) ? String(assignment[name]) : ''}
                onChange={(event) => {
                  give(
                    name,
                    event.target.value === '' ? null : Number(event.target.value),
                  )
                }}
              >
                <option value="">{t('wizard.abilities.assignEmpty')}</option>
                {assigned.includes(name) && (
                  <option value={String(assignment[name])}>{assignment[name]}</option>
                )}
                {left.map((value, index) => (
                  <option key={`${value}-${index}`} value={String(value)}>
                    {value}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                value={assignment[name]}
                onChange={(event) => {
                  onAssignment({ ...assignment, [name]: Number(event.target.value) })
                }}
              />
            )}
          </label>
        ))}
      </div>

      {invalid && (
        <ul className="app-problems" role="alert">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
    </fieldset>
  )
}
